import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, MessageCircle, Trash2, Send, NotebookPen } from "lucide-react";

const wordCount = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return Math.floor(s / 60) + "m ago";
  if (s < 86400) return Math.floor(s / 3600) + "h ago";
  if (s < 604800) return Math.floor(s / 86400) + "d ago";
  return new Date(iso).toLocaleDateString();
};

const Reaction = ({ comment, onChanged }) => {
  const { user } = useAuth();
  const [liked, setLiked] = useState(!!comment.liked_by_me);
  const [count, setCount] = useState(comment.like_count || 0);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!user) { toast.error("log in to like"); return; }
    if (busy) return;
    setBusy(true);
    const prev = { liked, count };
    setLiked(!liked); setCount(liked ? count - 1 : count + 1);
    try {
      const { data } = await api.post("/topic/comments/" + comment.id + "/like");
      setLiked(data.liked); setCount(data.count);
    } catch (e) {
      setLiked(prev.liked); setCount(prev.count);
      toast.error("could not like");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy} data-testid={"topic-like-" + comment.id} className={"flex items-center gap-1 text-[10px] uppercase tracking-widest font-outfit transition-colors " + (liked ? "text-hotpink" : "text-white/50 hover:text-hotpink")}>
      <Heart size={11} fill={liked ? "currentColor" : "none"} /> {count > 0 ? count : "like"}
    </button>
  );
};

const ReplyComposer = ({ entryId, onPosted }) => {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post("/topic/entries/" + entryId + "/comments", { body });
      setBody("");
      if (onPosted) onPosted();
    } catch (e) {
      toast.error("could not post");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex gap-2 mt-2" data-testid={"topic-reply-form-" + entryId}>
      <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="reply..." maxLength={600} className="flex-1 bg-ink border border-white/15 px-3 py-1.5 text-white font-outfit text-xs focus:border-hotpink focus:outline-none" />
      <button type="submit" disabled={busy || !body.trim()} className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-bold uppercase tracking-widest text-[10px] px-3 disabled:opacity-50">post</button>
    </form>
  );
};

const EntryCard = ({ entry, onChanged, idx }) => {
  const { user } = useAuth();
  const [replyOpen, setReplyOpen] = useState(false);
  const isOwn = user && user.id === entry.user_id;
  const isAdmin = user && user.is_admin;
  const remove = async () => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete("/topic/comments/" + entry.id);
      onChanged();
    } catch (e) { toast.error("could not delete"); }
  };
  const tilt = idx % 2 === 0 ? "rotate-[-1deg]" : "rotate-[1deg]";
  return (
    <div className={"bg-sticky_pink text-ink p-4 md:p-5 shadow-xl relative " + tilt} data-testid={"topic-entry-" + entry.id}>
      <div className="tape" />
      <div className="flex items-baseline gap-3 mb-2 flex-wrap">
        <span className="font-bold text-hotpink_dark text-base">{entry.user_name || "anon"}</span>
        <span className="text-ink/50 text-xs font-outfit">{timeAgo(entry.created_at)}</span>
      </div>
      <p className="scribble text-xl md:text-2xl text-ink leading-snug mb-3 whitespace-pre-wrap">"{entry.body}"</p>
      <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest font-outfit text-ink/60">
        <Reaction comment={entry} />
        {user && (
          <button onClick={() => setReplyOpen(!replyOpen)} className="hover:text-hotpink_dark flex items-center gap-1" data-testid={"topic-reply-btn-" + entry.id}>
            <MessageCircle size={11} /> reply {entry.replies?.length ? "(" + entry.replies.length + ")" : ""}
          </button>
        )}
        {(isOwn || isAdmin) && (
          <button onClick={remove} className="hover:text-red-600 flex items-center gap-1 ml-auto">
            <Trash2 size={11} /> delete
          </button>
        )}
      </div>
      {replyOpen && <ReplyComposer entryId={entry.id} onPosted={onChanged} />}
      {entry.replies && entry.replies.length > 0 && (
        <div className="mt-3 pt-3 border-t-2 border-dashed border-ink/15 space-y-2">
          {entry.replies.map(r => (
            <div key={r.id} className="bg-white/40 p-2" data-testid={"topic-reply-" + r.id}>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-bold text-hotpink_dark text-sm">{r.user_name}</span>
                <span className="text-ink/40 text-[10px] font-outfit">{timeAgo(r.created_at)}</span>
              </div>
              <p className="font-outfit text-sm text-ink whitespace-pre-wrap">{r.body}</p>
              <div className="flex items-center gap-3 mt-1">
                <Reaction comment={r} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function TopicOfWeek() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/topic/current");
      setData(data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    const wc = wordCount(body);
    if (wc > 50) { toast.error("keep it to 50 words or fewer"); return; }
    setBusy(true);
    try {
      await api.post("/topic/entries", { body });
      setBody(""); setComposing(false);
      toast.success("added to the thread ♡");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not post");
    } finally { setBusy(false); }
  };

  if (loading || !data) return null;
  if (!data.topic) return null;

  const wc = wordCount(body);
  const hasMine = !!data.my_entry_id;

  return (
    <div className="relative bg-coal border-2 border-hotpink/60 p-5 md:p-7 mb-10 overflow-hidden" data-testid="topic-of-week">
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-hotpink/15 blur-3xl rounded-full pointer-events-none" />
      <div className="flex items-center gap-2 mb-2">
        <NotebookPen className="text-hotpink" size={18} />
        <span className="text-[10px] uppercase tracking-[0.3em] font-outfit font-bold text-hotpink">Joey's question of the week</span>
      </div>
      <h2 className="brush-title text-3xl md:text-5xl text-white !leading-[1.6] md:!leading-[1.5] mb-4 tracking-wider [word-spacing:0.25em]" data-testid="topic-question">&ldquo;{data.topic.question}&rdquo;</h2>
      <p className="scribble text-white/70 text-lg rotate-[-1deg] inline-block tracking-wide [word-spacing:0.15em]">drop your story in 50 words. joey reads every one.</p>

      <div className="mt-5 flex items-start gap-3 flex-wrap">
        {!user && (
          <Link to="/login" data-testid="topic-login-link" className="font-outfit text-white/70 hover:text-hotpink text-sm underline underline-offset-4 decoration-hotpink/60 hover:decoration-hotpink transition-colors">log in to share your experience →</Link>
        )}
        {user && !hasMine && !composing && (
          <button onClick={() => setComposing(true)} data-testid="topic-open-composer" className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-5 py-3 shadow-[4px_4px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_#FFFFFF] transition-all flex items-center gap-2">
            <Send size={14} /> share your story
          </button>
        )}
        {user && hasMine && (
          <span className="scribble text-hotpink text-lg">you've already shared on this one ♡ scroll down to see it</span>
        )}
        <span className="text-white/40 text-xs font-outfit ml-auto self-center">{data.entry_count} {data.entry_count === 1 ? "story" : "stories"} so far</span>
      </div>

      {composing && (
        <form onSubmit={submit} className="mt-4" data-testid="topic-composer">
          <textarea autoFocus value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="he had a spreadsheet ranking his dates by ROI..." className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm md:text-base focus:border-hotpink focus:outline-none resize-none" />
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className={"text-xs font-outfit " + (wc > 50 ? "text-red-400" : "text-white/50")}>{wc}/50 words</span>
            <button type="submit" disabled={busy || !body.trim() || wc > 50} className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-5 py-2 disabled:opacity-50 ml-auto" data-testid="topic-submit">{busy ? "..." : "post"}</button>
            <button type="button" onClick={() => { setComposing(false); setBody(""); }} className="text-white/50 text-xs uppercase tracking-widest hover:text-hotpink">cancel</button>
          </div>
        </form>
      )}

      {data.entries.length > 0 && (
        <div className="mt-7 grid md:grid-cols-2 gap-4">
          {data.entries.map((e, i) => <EntryCard key={e.id} entry={e} idx={i} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
